import {OrderedMap, Set as ImmSet} from 'immutable';
import {applyBrand} from '@packages/utilities/type-safety';
import {
  generateNKeysBetween as generateNKeysBetween_,
  generateKeyBetween as generateKeyBetween_,
} from './fractional-indexing';
import type {
  SwatchType,
  CssVariablesStateType,
} from '@packages/systems/core/utils/StyleTypes';
import type {WebflowCssPropertyFull} from '@packages/systems/style/properties';
import {generateGuid} from '@packages/utilities/guid';
import {incrementString} from '@packages/utilities/string-utils';
import {assoc, identity, omit} from '@packages/utilities/fp/utils';
import {
  fixRgba,
  getHex,
  getOpacity,
  parseColor,
} from '@packages/systems/core/utils/ColorUtils';
import type {Font} from '@packages/systems/core/constants/Fonts';
import {safeFamilyName} from '@packages/systems/core/utils/FontUtils';
import {reportBug, reportBugOnce} from '@packages/utilities/reporter';
import {Plugins} from '@packages/systems/wfdl';
import {getPluginName} from '@packages/systems/wfdl-Qualified';
import {
  StyleBlock,
  StyleBlockVariant,
} from '@packages/systems/style-legacy/records';
import {once, weakMemo} from '@packages/utilities/memo';
import {BreakpointID} from '@packages/systems/style/types';
import type {
  AnyVariableType,
  VariableCollectionId,
  VariableModeId,
  VariableCollectionMap,
  VariableID,
  VariableType,
  AnyVariableValue,
  VariableCollection,
  GroupKey,
  GroupedVariables,
  AnyStylePrimitive,
  CustomRawPrimitive,
  ServerAnyVariableType,
  ServerVariableCollection,
  VariableModesMap,
  ColorVariable,
  VariableMapPojo,
  StyleValueValidationStrategy,
  VariableReference,
} from './types';

//////////////////////////////////////////////
// For StyleVariableBindMenuTypes
//
import * as React from 'react';
import {
  MasksConfig,
  RenderChildrenProps,
} from '@packages/systems/design-system/PopoverBridge';
import type {OnChangeMeta} from '@packages/shared/LegacyStyleSystem/utils/StylePanelUtils';
import {Box} from '@packages/utilities/box';
//
//////////////////////////////////////////////

export * as Helpers from './testHelpers';
export * from './serializers';
export * from './types';
export * from './validations';
export * from './unit';

/**
 * Type guard that checks whether an unknown value is a style variable reference.
 */
export function isVariableReference(
  value: unknown
): value is VariableReference {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as Record<string, unknown>).type === 'ref' &&
    typeof (value as Record<string, unknown>).value === 'object' &&
    (value as Record<string, unknown>).value !== null &&
    typeof ((value as Record<string, unknown>).value as Record<string, unknown>)
      .variableId === 'string'
  );
}

/**
 * Hard-coded mode "id" used to denote the application of a base mode.
 */
export const BASE_VARIABLE_MODE_ID: VariableModeId = 'base';

/**
 * Default display name for the base mode when no other label is configured.
 */
export const BASE_VARIABLE_MODE_DEFAULT_NAME = 'Base mode';

/**
 * Returns the display name for the base mode of the given collection.
 *
 * Resolution order:
 * 1. A site-level rename, if persisted as a metadata entry under
 *    `BASE_VARIABLE_MODE_ID` on `collection.modes`.
 * 2. The caller-provided `fallback`, intended for context-specific overrides
 *    (e.g. a component-author label for a particular prop).
 * 3. `BASE_VARIABLE_MODE_DEFAULT_NAME`.
 *
 * A soft-deleted (`deleted: true`) stored entry is ignored, so deletion
 * reverts the name to the next step in the order.
 */
export function getBaseModeName(
  collection: Pick<VariableCollection, 'modes'> | null | undefined,
  fallback?: string
): string {
  const stored = collection?.modes?.[BASE_VARIABLE_MODE_ID];
  if (stored != null && !stored.deleted && stored.name) {
    return stored.name;
  }
  return fallback || BASE_VARIABLE_MODE_DEFAULT_NAME;
}

/**
 * Hard-coded mode "id" used to denote that all variables in a collection
 * should inherit from the parent element rather than being overridden with
 * specific mode values.
 */
export const INHERIT_VARIABLE_MODE_ID: VariableModeId = 'inherit';

/**
 * The default name given to a site's default variable collection.
 */
export const DEFAULT_VARIABLE_COLLECTION_NAME = 'Base collection' as const;

/**
 * Per-site variable limit: an operational guardrail that keeps variable
 * document counts bounded — large counts inflate branch clones, library sync,
 * and site backups/restores (see STYL variable-bloat incidents).
 *
 * The limit lives in a Statsig dynamic config so it can be tuned (or disabled)
 * at runtime. Both the server (which enforces the limit) and the Designer
 * (which proactively disables variable creation once the limit is reached) read
 * the same config so they never diverge. A value `<= 0` means "no limit" — this
 * doubles as a kill-switch, and the config defaults to disabled so shipping the
 * code is a no-op until the config is raised in Statsig.
 */
export type GlobalVariableLimits = {
  maxVariablesPerSite: number;
};

export const DC_VARIABLES_GLOBAL_LIMITS = 'dc-styl-variables-per-site-limit';

export const VARIABLES_MAX_GLOBAL_LIMITS: GlobalVariableLimits = {
  maxVariablesPerSite: 0,
};

export function createVariableModeId(value: string): VariableModeId {
  return value as VariableModeId;
}

export const variableCollectionMapFromArray = (
  variableCollections: VariableCollection[]
): VariableCollectionMap => {
  const result = emptyVariableCollectionMap.withMutations((map) => {
    for (const collection of variableCollections) {
      map.set(collection.id, collection);
    }
  });
  return applyBrand(result);
};

export function sortByVariableCollectionOrder(
  variableCollectionMap: VariableCollectionMap
): VariableCollectionMap {
  return variableCollectionMap.sort(orderSortComparator);
}

/**
 * Given all variable collections and a variable that may or may not have a
 * `collectionId` specified, return the variable collection that the variable
 * belongs to.
 *
 * In the case that a variable does not have a `collectionId`, we assign the
 * variable to the default collection and thus return the default collection
 * (either for the site, or for the library that the variable belongs to).
 *
 * This function will only return `null` in the case where a variable's
 * `collectionId` explicitly points to a nonexisting collection, or if there is
 * no default collection associated with the variable's site/library.
 */
export function getVariableCollection(
  variable: AnyVariableType,
  variableCollections: VariableCollectionMap
): VariableCollection | null {
  // Happy case: Variable has a collection ID, so we look up its collection and
  // return it, or `null` if the ID doesn't resolve to a known collection:
  if (variable.collectionId != null) {
    return variableCollections.get(variable.collectionId) ?? null;
  }

  // Otherwise, if the variable belongs to the site but has no collection, we
  // return the site's default collection or `null` if no default collection
  // exists:
  if (variable.origin == null) {
    return (
      variableCollections.find(
        (collection) =>
          collection.isDefault === true && collection.origin == null
      ) ?? null
    );
  }

  // Finally, if we're working with a variable from a library, we must return
  // the default collection that belongs to the variable's library:
  const libraryPluginName = getPluginName(variable.origin);
  return (
    variableCollections.find(
      (collection) =>
        collection.isDefault === true &&
        collection.origin != null &&
        getPluginName(collection.origin) === libraryPluginName
    ) ?? null
  );
}

export const VARIABLES_PANEL = 'variables' as const;

export const generateVariableId = (guidGenerator = generateGuid): VariableID =>
  'variable-' + guidGenerator();

export const generateModeId = (generate = generateGuid): VariableModeId =>
  'mode-' + generate();

export const generateCollectionId = (
  generate = generateGuid
): VariableCollectionId => 'collection-' + generate();

export const generateVariableName = (
  type: VariableType,
  groupName?: string
): string => {
  switch (type) {
    case 'color':
    case 'font-family':
    case 'number':
    case 'percentage':
      return groupName ? `${groupName}/${type}` : type;

    case 'length':
      return groupName ? `${groupName}/size` : 'size';

    default: {
      const _exhaustiveCheck: never = type;
      throw new Error(`Unexpected variable type: ${type}`);
    }
  }
};

/**
 * Sort function that sorts collections (variable, variableCollections, etc) by their
 * `order` field lexicographically in ascending order.
 *
 * `null` values for `order` maintain their positions relative to other `null`s,
 * but are pulled to the front of the list.
 */
function orderSortComparator<
  T extends {
    order: string | null;
  },
>(a: T, b: T): number {
  const orderA = a.order;
  const orderB = b.order;

  // Maintain original order in the event we have equal order values (e.g. when
  // both are `null`) - technically we don't need this extra check early on, but
  // without it, if we get the order of the checks below wrong, we risk changing
  // the presented order of items that do not have `order` yet.
  if (orderA === orderB) return 0;

  // Sort `null`s to the front of the list:
  if (orderA == null) return -1;
  if (orderB == null) return 1;

  if (orderA < orderB) return -1;
  if (orderA > orderB) return 1;

  return 0;
}

/**
 * Given a `VariableMap`'s `data` field (an `OrderedMap`), return a new
 * `OrderedMap` that is sorted by the `order` field on each variable.
 */
function sortByVariableOrder(
  variables: OrderedMap<VariableID, AnyVariableType>
): OrderedMap<VariableID, AnyVariableType> {
  return variables.sort(orderSortComparator);
}

/**
 * Inspired mostly by `Immutable.OrderedMap`, `VariableMap` is an immutable data
 * structure used to ensure that when iterated over, variables in the map will
 * be sorted by their `order` field.
 *
 * Note: This is not an _extension_ of `Immutable.OrderedMap`, but rather a
 * _wrapper_ around one.
 *
 * - Only a minimal subset of `Immutable.OrderedMap` methods are implemented
 *   manually.
 * - Unlike `Immutable.OrderedMap`, there is no `set(id, variable)` method.
 *   Instead, an `upsert(variable)` method is provided which ensures that
 *   `variable.id` is used as the variable's key in the underlying map.
 * - For convenience, an `upsertAll(variables)` method is also provided which
 *   will efficiently insert many variables at once and produce a new
 *   `VariableMap` result.
 */
export class VariableMap implements Iterable<AnyVariableType> {
  /**
   * Returns a new `VariableMap` containing all variables from the provided
   * `Iterable<AnyVariableType>` argument.
   */
  static from(variables: Iterable<AnyVariableType>): VariableMap {
    const entries: Array<[VariableID, AnyVariableType]> = [];
    for (const variable of variables) {
      entries.push([variable.id, variable]);
    }
    return new VariableMap(OrderedMap(entries));
  }

  /**
   * Underlying `OrderedMap` data, which may or may not be sorted.
   */
  private data: OrderedMap<VariableID, AnyVariableType>;

  /**
   * A flag that indicates whether or not the underlying `data` `OrderedMap` is
   * already sorted.
   */
  private sorted: boolean = false;

  private constructor(data: OrderedMap<VariableID, AnyVariableType>) {
    this.data = data;
  }

  get size(): number {
    return this.data.size;
  }

  count(): number {
    return this.data.count();
  }

  isEmpty(): boolean {
    return this.data.isEmpty();
  }

  get(id: VariableID): AnyVariableType | undefined {
    return this.data.get(id);
  }

  /**
   * Returns a new variable map with the given variable added to the map, or
   * updated if a variable with the same id already existed in the map.
   */
  upsert(variable: AnyVariableType): VariableMap {
    return new VariableMap(this.data.set(variable.id, variable));
  }

  /**
   * Like `VariableMap.upsert`, but takes an array of variables to be upserted.
   */
  upsertAll(variables: Iterable<AnyVariableType>): VariableMap {
    const nextData = this.data.withMutations((data) => {
      for (const variable of variables) {
        data.set(variable.id, variable);
      }
    });
    return new VariableMap(nextData);
  }

  /**
   * Removes the variable from the map completely.
   *
   * Note: This is NOT the same as marking a variable as deleted from the user's
   * perspective. To do that, set the `deleted` flag to `true`.
   */
  delete(id: VariableID): VariableMap {
    return new VariableMap(this.data.delete(id));
  }

  has(id: VariableID): boolean {
    return this.data.has(id);
  }

  //
  // Any methods below are sensitive to order, and thus must each call
  // `this.sortIfNeeded()` before calling into the underlying `OrderedMap`
  // implementation.
  //

  /**
   * Ensures that this `VariableMap`'s underlying `data` collection is sorted
   * by variable `order` field.
   */
  private sortIfNeeded() {
    if (this.sorted) {
      return;
    }

    this.data = sortByVariableOrder(this.data);
    this.sorted = true;
  }

  [Symbol.iterator](): Iterator<AnyVariableType> {
    this.sortIfNeeded();
    return this.data.values();
  }

  filter(predicate: (variable: AnyVariableType) => boolean): VariableMap {
    this.sortIfNeeded();
    return new VariableMap(this.data.filter(predicate));
  }

  find(
    predicate: (variable: AnyVariableType, key: string) => boolean
  ): AnyVariableType | undefined {
    this.sortIfNeeded();
    return this.data.find(predicate);
  }

  findLast(
    predicate: (variable: AnyVariableType, key: string) => boolean
  ): AnyVariableType | undefined {
    this.sortIfNeeded();
    return this.data.findLast(predicate);
  }

  forEach(callback: (v: AnyVariableType) => unknown): void {
    this.sortIfNeeded();
    this.data.forEach(callback);
  }

  keys(): Iterable<VariableID> {
    this.sortIfNeeded();
    return this.data.keys();
  }

  map<T>(mapper: (variable: AnyVariableType) => T): Iterable<T> {
    this.sortIfNeeded();
    // @ts-expect-error - `Immutable.Iterable` is compatible with `Iterable` but its types do not reflect this properly
    return this.data.valueSeq().map(mapper);
  }

  reduce<R>(
    reducer: (currentValue: R, variable: AnyVariableType, id: VariableID) => R,
    initialValue: R
  ): R {
    this.sortIfNeeded();
    return this.data.reduce(reducer, initialValue);
  }

  toArray(): AnyVariableType[] {
    this.sortIfNeeded();
    return this.data.toArray();
  }

  toJS(): {[id: VariableID]: AnyVariableType} {
    this.sortIfNeeded();
    return this.data.toJS();
  }

  /**
   * Internal static method which is used by a custom Jest serializer.
   *
   * Ensures the printed order matches the order that would be encountered when
   * iterating over
   */
  static __jestPrint(
    value: VariableMap,
    printer: (val: unknown) => string
  ): string {
    // First, create a clone of the value so we avoid any internal data
    // structure mutations when we force it to be sorted below:
    const clone = VariableMap.from(value);

    // For most use-cases, we want snapshot output to hide details like `data`
    // and `sorted`, and to print in the order that will be seen by any methods
    // that iterate over variables in the map, so we manually sort here:
    clone.sortIfNeeded();

    // We then use Jest's built-in printer to print the now-sorted
    // `Immutable.OrderedMap` data directly, slicing off the
    // `Immutable.OrderedMap` label and replacing it with `VariableMap`:
    return `VariableMap ${printer(clone.data).slice('Immutable.OrderedMap '.length)}`;
  }
}

export const printPreviewLabel = (
  val: AnyVariableValue,
  variableId: VariableID,
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap']
): string | VariableResolutionError => {
  switch (val.type) {
    case 'length': {
      return `${val.value.value}${val.value.unit}`;
    }

    case 'number': {
      return val.value.value.toString();
    }

    case 'percentage': {
      return `${val.value.value}%`;
    }

    case 'font-family': {
      return val.value;
    }

    case 'color': {
      const parsedColor = parseColor(val.value);
      if (!parsedColor) {
        reportBugOnce(
          'InvalidColorVariableValue',
          `Could not parse color value "${val.value}" of variable with id ${variableId}`,
          {team: 'dse', ticket: 'https://webflow.atlassian.net/browse/DSE-7320'}
        );
        return val.value;
      }

      return parsedColor;
    }

    case 'raw': {
      const lessValue = val.value;
      // Then replace any @var_... with their correct CSS variable names:
      return replaceVariablePlaceholdersForCSSOM(
        variables,
        variableCollections,
        slugMap,
        lessValue
      );
    }

    case 'ref': {
      const ref = val.value;
      const label = variables.get(ref.variableId)?.name;
      if (label == null) {
        return new VariableResolutionError(
          'unknown',
          `Could not resolve variable reference; variableId: ${variableId}, ref: ${JSON.stringify(
            ref
          )}`
        );
      }
      return label;
    }

    default: {
      const _exhaustiveCheck: never = val;
      throw new Error(`Unexpected VariableValue shape: ${JSON.stringify(val)}`);
    }
  }
};

/**
 * Looks up a human-readable label for a given variable, using reference names
 * when applicable.
 */
export const resolvePreviewLabel = (
  variables: VariableMap,
  variableId: VariableID,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'],
  modeId?: VariableModeId
): string | VariableResolutionError => {
  const variable = variables.get(variableId);

  if (!variable) {
    return new VariableResolutionError(
      'unknown',
      `Could not resolve variable; variableId: ${variableId}`
    );
  }
  let val: AnyVariableValue | undefined = modeId
    ? variable.modes[modeId]
    : undefined;
  if (!val) {
    val = variable.value;
  }

  return printPreviewLabel(
    val,
    variableId,
    variables,
    variableCollections,
    slugMap
  );
};

/**
 * Builds the searchable text for a variable, including its name, resolved
 * preview label, and the name of the collection it belongs to. This is used
 * by fuzzy search across the Variables Panel, Style Panel binding menu, and
 * CSS custom value autocomplete.
 *
 * For color variables, the preview label provides a normalized CSS keyword
 * name when available (e.g. `whitesmoke`), while extra terms preserve the
 * canonical hex and raw stored forms. Without this, a variable stored as
 * `#F5F5F5` would only match searches for `whitesmoke` because the preview
 * label normalizes to the shorter keyword form (POP-3205 / PQ-1277).
 */
export const getVariableSearchableText = (
  variable: AnyVariableType,
  allVariables: VariableMap,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'],
  modeId?: VariableModeId
): string => {
  const collection = getVariableCollection(variable, variableCollections);
  const collectionName = collection?.name ?? '';
  const previewLabel = resolvePreviewLabel(
    allVariables,
    variable.id,
    variableCollections,
    slugMap,
    modeId
  );
  if (previewLabel instanceof Error) {
    return collectionName
      ? variable.name + ' ' + collectionName
      : variable.name;
  }
  const extraSearchTerms = getExtraColorSearchTerms(
    variable,
    previewLabel,
    modeId
  );
  const previewSection = extraSearchTerms
    ? previewLabel + ' ' + extraSearchTerms
    : previewLabel;
  return collectionName
    ? variable.name + ' ' + previewSection + ' ' + collectionName
    : variable.name + ' ' + previewSection;
};

/**
 * Returns canonical hex and raw stored search terms for a color variable. The
 * CSS keyword form, when available, is already provided by the preview label.
 * Returns an empty string when the variable is not a resolved color or no
 * extra form is available.
 */
const getExtraColorSearchTerms = (
  variable: AnyVariableType,
  previewLabel: string,
  modeId: VariableModeId | undefined
): string => {
  if (variable.type !== 'color') {
    return '';
  }
  // Match `resolvePreviewLabel`'s mode fallback: when the variable has no
  // override for the active mode, fall back to the base value. Otherwise
  // the preview shows the base color but the extra search terms would be
  // dropped, so PQ-1277 would still repro for mode-scoped surfaces.
  const val = (modeId ? variable.modes[modeId] : undefined) ?? variable.value;
  if (!val || val.type !== 'color') {
    return '';
  }
  const rawValue = val.value;
  if (typeof rawValue !== 'string' || !rawValue.trim()) {
    return '';
  }
  // Normalize with `fixRgba` so the validity gate below and the hex
  // extraction share the same parsing path. Without this, a legacy value
  // like `rgba(255,255,255)` (which `fixRgba` is designed to repair) would
  // pass `parseColor` but `getHex` would fall back to tinycolor's `#000000`
  // default, silently indexing a white variable under `#000000`.
  const normalized = fixRgba(rawValue);
  if (typeof normalized !== 'string') {
    return '';
  }
  // `parseColor` returns `null` for invalid colors; use it as a validity
  // gate before pulling the canonical hex to avoid indexing meaningless
  // strings as `#000000` (tinycolor's default).
  if (parseColor(normalized) === null) {
    return '';
  }
  // `getHex` drops alpha, so `transparent` and `rgba(255,0,0,0.5)` would
  // both collapse to the opaque hex form and become findable by an
  // unrelated hex search. For alpha < 1, skip the hex term but still index a
  // non-hex raw value below so a user searching by their original text (e.g.
  // `hsla(0, 100%, 50%, 0.5)`) can still find the variable.
  // Tinycolor treats CSS Color 4 slash-alpha syntax as opaque, so read its
  // numeric alpha directly before adding a canonical hex search term. If the
  // slash value is not numeric, conservatively skip that term. This does not
  // change the preview label's pre-existing normalization.
  const slashAlpha = normalized.match(/\/\s*(\d*\.?\d+)(%)?\s*\)$/);
  const isOpaque = slashAlpha
    ? Number(slashAlpha[1]) >= (slashAlpha[2] ? 100 : 1)
    : !normalized.includes('/') && getOpacity(normalized) >= 1;
  const hex = isOpaque ? getHex(normalized) : '';
  const hexWithHash = hex
    ? hex.startsWith('#')
      ? hex.toLowerCase()
      : '#' + hex.toLowerCase()
    : '';
  const previewLower = previewLabel.toLowerCase();
  const terms: string[] = [];
  // Include the hex form when the preview label instead normalizes to a CSS
  // keyword or the shortened hex3 form (e.g. `#0066cc` renders as `#06c`).
  if (hexWithHash && previewLower !== hexWithHash) {
    terms.push(hexWithHash);
  }
  // If the raw stored value differs from both the preview label and the
  // canonical hex (e.g. user typed a CSS keyword like `whitesmoke` directly,
  // or typed `#F5F5F5` and preview shows `whitesmoke`), include it as well.
  const rawLower = rawValue.trim().toLowerCase();
  // Translucent 4/8-digit hex: FlexSearch's full tokenizer would also match
  // the opaque prefix, so skip it.
  const isTranslucentHex =
    !isOpaque && /^#(?:[0-9a-f]{4}|[0-9a-f]{8})$/.test(rawLower);
  if (
    rawLower &&
    rawLower !== previewLower &&
    rawLower !== hexWithHash &&
    !isTranslucentHex
  ) {
    terms.push(rawLower);
  }
  return terms.join(' ');
};

/**
 * Special error class that is thrown to distinguish errors thrown by style variables functionality from any other errors.
 */
export class VariableRenderError extends Error {}

/**
 * Given variable id and variable map, returns a string representation of the variable value that is used in compiled styles.
 */
export const renderCssValue = (
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'],
  variableId: VariableID,
  fonts: Font[],
  mut_seenVariables?: Set<VariableID>
): string | VariableRenderError => {
  const val = variables.get(variableId)?.value;

  if (!val) {
    return new VariableRenderError(
      `Could not resolve variable; variableId: ${variableId}`
    );
  }
  return renderAnyCssValue(
    val,
    variables,
    variableCollections,
    slugMap,
    fonts,
    mut_seenVariables
  );
};

/**
 * Strip trailing semicolons and whitespace from a raw variable value.
 *
 * Variable custom values should never contain a trailing `;` (the UI strips it
 * on input via `cleanInputValue`), but corrupt values exist in the wild
 * (DSYS-980). Published CSS tolerates the stray `;`, but the Designer's
 * LESS/CSSOM render paths drop the whole declaration. Sanitizing at render
 * time keeps every consumer valid without changing well-formed values.
 */
const stripTrailingSemicolons = (value: string): string =>
  value.replace(/[;\s]*$/, '');

/**
 * Given pre-resolved `AnyVariableValue`, render the appropriate CSS string.
 */
export const renderAnyCssValue = (
  val: AnyVariableValue,
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'],
  fonts: Font[],
  mut_seenVariables?: Set<VariableID>
) => {
  switch (val.type) {
    case 'length': {
      return `${val.value.value}${val.value.unit}`;
    }
    case 'color': {
      return val.value;
    }
    case 'font-family': {
      return safeFamilyName(val.value, fonts);
    }
    case 'number': {
      return val.value.value.toString();
    }
    case 'percentage': {
      return `${val.value.value}%`;
    }
    case 'ref': {
      mut_seenVariables?.add(val.value.variableId);
      return `var(--${renderCssVariableName(
        variables,
        variableCollections,
        slugMap,
        val.value.variableId
      )})`;
    }
    case 'raw': {
      // Strip trailing semicolons (DSYS-980): corrupt custom values can carry a
      // trailing `;` that the Designer's render paths choke on. The UI strips it
      // on input (`cleanInputValue`); we also sanitize here while rendering.
      const lessValue = stripTrailingSemicolons(val.value);
      // Then replace any @var_... with their correct CSS variable names:
      return replaceVariablePlaceholdersForPublish(
        variables,
        variableCollections,
        slugMap,
        lessValue,
        mut_seenVariables
      );
    }
    default: {
      const _exhaustiveCheck: never = val;
      return new VariableRenderError(
        `Unexpected VariableValue shape: ${JSON.stringify(val)}`
      );
    }
  }
};

export class VariableResolutionError extends Error {
  type: 'circular' | 'mismatched-type' | 'unknown';
  constructor(
    type: typeof VariableResolutionError.prototype.type,
    message: string
  ) {
    super(message);
    this.type = type;
  }
}

/**
 * Following variable references until a concrete value is arrived at.
 *
 * This function will recurse into raw values, replacing any `@var_...`
 * references with their own resolved preview values.
 */
export const resolvePreviewValue = <
  T extends AnyStylePrimitive = AnyStylePrimitive,
>(
  variables: VariableMap,
  variableId: VariableID,
  modeId?: VariableModeId
): T | CustomRawPrimitive | VariableResolutionError => {
  return resolveValue_(
    variables,
    variableId,
    modeId ?? null,
    true,
    ImmSet<string>()
  );
};

/**
 * Resolve a value from a variable such that the return value is either a
 * concrete style primitive, or a raw value.
 *
 * Unlike `resolvePreviewValue`, this function does **not** recurse into raw
 * values to replace `@var_...` references with their own resolved preview
 * values. In other words, bindings inside raw values will be retained in the
 * result.
 */
export const resolveUnlinkedValue = (
  variables: VariableMap,
  variableId: VariableID,
  modeId: VariableModeId | null
): AnyVariableValue | VariableResolutionError => {
  return resolveValue_(variables, variableId, modeId, false, ImmSet<string>());
};

const resolveValue_ = <T extends AnyStylePrimitive = AnyStylePrimitive>(
  variables: VariableMap,
  variableId: VariableID,
  // This parameter really needs to be a full set of all applicable modes,
  // mapped by collection, since there could be multiple variables we need to
  // resolve while resolving the full value, which means there could be multiple
  // _collections_ involved, which means each collection's active mode must be
  // determined in order to resolve the preview value.
  //
  // Concretely, this should probably be something like:
  //
  // ```ts
  // appliedModes: Map<VariableCollectionId, VariableModeId | null>
  // ```
  //
  // Tracking: https://webflow.atlassian.net/browse/STYL-1227
  modeId: VariableModeId | null,
  recurseIntoRawValues: boolean,
  seen: Immutable.Set<string>
): T | CustomRawPrimitive | VariableResolutionError => {
  if (seen.has(variableId)) {
    return new VariableResolutionError(
      'circular',
      `Circular reference encountered: variableId: ${variableId}`
    );
  }

  const variable = variables.get(variableId);
  if (variable == null) {
    return new VariableResolutionError(
      'unknown',
      `Could not resolve variable; variableId: ${variableId}`
    );
  }

  let val: AnyVariableValue | null | undefined =
    (modeId ? variable.modes[modeId] : null) || variable.value;

  seen = seen.add(variable.id);

  while (val != null && val.type === 'ref') {
    const ref = val.value;
    if (seen.has(val.value.variableId)) {
      return new VariableResolutionError(
        'circular',
        `Circular reference encountered: variableId: ${variableId}`
      );
    }

    seen = seen.add(ref.variableId);

    const nextVariable = variables.get(ref.variableId);
    val = (modeId ? nextVariable?.modes[modeId] : null) || nextVariable?.value;
  }

  if (val == null) {
    return new VariableResolutionError(
      'unknown',
      `Could not resolve variable; variableId: ${variableId}`
    );
  }

  if (val.type === 'raw') {
    // We may or may not want to recurse into raw values and replace nested
    // `@var_...` references...
    if (!recurseIntoRawValues) {
      // If we don't want to recurse, we can simply pass the raw value as-is:
      return val;
    }

    // Otherwise, we need to replace each `@var_...` reference with its resolved
    // value:
    try {
      const replaced = val.value.replaceAll(
        VARIABLE_PLACEHOLDER_REGEX,
        (match: string, nestedVariableId: string) => {
          const previewValue = resolveValue_(
            variables,
            nestedVariableId,
            modeId,
            recurseIntoRawValues,
            seen
          );
          if (previewValue instanceof VariableResolutionError) {
            throw previewValue;
          }
          // Result should now be either a primitive value, or a raw value whose
          // contents replaced any `@var_...` references with their rendered
          // equivalents:
          const renderedValue = renderAnyCssValue(
            previewValue,
            // We can use empty variable/variableCollection/slug maps because we
            // shouldn't be dealing with any variable references at this point:
            emptyVariableMap,
            emptyVariableCollectionMap,
            {},
            // We need to pass `fonts` in here in order to render resolved
            // font-family variables correctly.
            //
            // Tracking: https://webflow.atlassian.net/browse/STYL-1230
            []
          );
          if (renderedValue instanceof VariableRenderError) {
            throw renderedValue;
          }
          return renderedValue;
        }
      );

      return {type: 'raw', value: replaced};
    } catch (e: unknown | VariableResolutionError) {
      if (e instanceof VariableResolutionError) {
        return e;
      }
      throw e;
    }
  }

  if (val.type !== variable.type) {
    return new VariableResolutionError(
      'mismatched-type',
      `Unmatched token type: Expected: ${variable.type}; Actual: ${val.type}`
    );
  }

  return val as T;
};

/**
 * Given a variable map, returns a new variable map with the given variable added to the map.
 *
 * @deprecated Use the `variableMap.upsert(variable)` method instead.
 */
export const upsertVariable = (
  variables: VariableMap,
  variable: AnyVariableType
): VariableMap => {
  return variables.upsert(variable);
};

/**
 * Give a variable map and an array of variables, returns a new variable map with the new variables added
 *
 * @deprecated Use the `variableMap.upsertAll(variables)` method instead.
 */
export const upsertMultipleVariables = (
  variableMap: VariableMap,
  variables: AnyVariableType[]
): VariableMap => {
  return variableMap.upsertAll(variables);
};

/**
 * Given current variable map and an updated variable, returns null if the variable can be updated without errors,
 * otherwise returns an error object.
 */
export const validateVariableUpdate = (
  variables: VariableMap,
  variable: AnyVariableType
): null | VariableResolutionError => {
  const newVariables = upsertVariable(variables, variable);
  const resolvedVariableValue = resolvePreviewValue(newVariables, variable.id);
  if (resolvedVariableValue instanceof VariableResolutionError) {
    return resolvedVariableValue;
  }
  return null;
};

/**
 * Given current variable map and an updated variable, returns null if the variable can be updated without errors,
 * otherwise returns an error object. Use with modes/non-base values.
 */
export const validateVariableModeValueUpdate = (
  variables: VariableMap,
  variable: AnyVariableType,
  value: AnyVariableValue | null,
  modeId: VariableModeId
): null | VariableResolutionError => {
  if (!variable.modes) return null;
  const nextModes =
    // If `null`, clear the value override so the mode will derive its value from the base/default mode.
    value == null
      ? omit([modeId])(variable.modes)
      : {
          ...variable.modes,
          [modeId]: value,
        };

  const nextVariables = variables.upsert({
    ...variable,
    modes: nextModes,
  });
  const resolvedVariableValue = resolvePreviewValue(
    nextVariables,
    variable.id,
    modeId
  );
  if (resolvedVariableValue instanceof VariableResolutionError) {
    return resolvedVariableValue;
  }
  return null;
};

/**
 * Given current variable map and an updated bulk variables...
 * Returns null if the variable can be updated without errors,
 * otherwise returns an error object.
 */
export const validateVariablesUpdate = (
  variables: VariableMap,
  nextVariables: AnyVariableType[] | null
): null | VariableResolutionError => {
  if (!nextVariables) {
    return null;
  }
  for (const variable of nextVariables) {
    const error = validateVariableUpdate(variables, variable);

    // Quick fail at first error.
    if (error) return error;
  }

  return null;
};

const markVariableAsDeleted = <T extends AnyVariableType = AnyVariableType>(
  variable: T
): T => {
  return {...variable, deleted: true};
};

/**
 * Given a `VariableMap` and a `VariableID`, marks the variable matching the provided id as deleted
 * @param {VariableMap} variables
 * @param {VariableID} variableId
 */
export const deleteVariable = (
  variables: VariableMap,
  variableId: VariableID
): VariableMap => {
  const variable = variables.get(variableId);
  if (!variable) {
    return variables;
  }
  return variables.upsert(markVariableAsDeleted(variable));
};

/**
 * Given an pojo of variables with the variables as values and their ids as keys,
 * returns a `VariableMap` with the variables as values and their ids as keys.
 *
 * @deprecated Use `VariableMap.from(Object.values(pojo))` instead
 */
export const variableMapFromPojo = (pojo: VariableMapPojo): VariableMap =>
  VariableMap.from(Object.values(pojo));

/**
 * Given an array of variables, returns a `VariableMap` with the variables as values and their ids as keys.
 *
 * @deprecated Use `VariableMap.from(array)` instead
 */
export const variableMapFromArray = (
  array: Array<AnyVariableType>
): VariableMap => VariableMap.from(array);

/**
 * Variable map with no variables
 */
export const emptyVariableMap = VariableMap.from([]);

/**
 * Variable collection map with no collections
 */
export const emptyVariableCollectionMap: VariableCollectionMap =
  applyBrand(OrderedMap());

/**
 * Given current variable map and proposed new variable, returns a new variable with a name that is guaranteed to be unique
 */
export const createVariableWithoutRepeatedName = (
  variables: VariableMap,
  variable: AnyVariableType
): AnyVariableType => {
  const collectionId = variable.collectionId;
  const existingVariableNames = variables.reduce<Array<string>>((acc, v) => {
    if (!v.deleted && v.origin == null && v.collectionId === collectionId)
      acc.push(v.name);
    return acc;
  }, []);

  const newName = incrementString(
    existingVariableNames,
    variable.name,
    '-',
    sanitizeForVariableName
  );
  if (newName !== variable.name) {
    return {
      ...variable,
      name: newName,
    };
  }

  return variable;
};

//
// CSS/Style System integrations and utilities
//
// These are based heavily on the existing Global Swatch implementations.
//

const VARIABLE_PREFIX = 'var_';
const VARIABLE_PLACEHOLDER_PREFIX = `@${VARIABLE_PREFIX}`;

export const VARIABLE_PLACEHOLDER_REGEX = /@var_([\w\d-_]+)/g;
const VARIABLE_DEFINITION_PREFIX = 'def_';
const VARIABLE_DEFINITION_PLACEHOLDER_PREFIX = `@${VARIABLE_DEFINITION_PREFIX}`;
const VARIABLE_DEFINITION_PLACEHOLDER_REGEX = /@def_([\w\d-_]+)(:[^;]+;)/g;
const VARIABLE_DEFINITION_PROPERTY_PLACEHOLDER_REGEX = /@def_([\w\d-_]+)/g;

/**
 * Generate a string value representing a variable reference.
 *
 * These "placeholder" values are swapped out by the CSS renderer with an
 * appropriate value depending on the environment or other contextual data.
 */
export function variablePlaceholder(id: VariableID): string {
  return `${VARIABLE_PLACEHOLDER_PREFIX}${id}`;
}

/**
 * Generate a string value representing a variable (re-)definition.
 *
 * These "placeholder" values are swapped out by the CSS renderer with an
 * appropriate value depending on the environment or other contextual data.
 */
export function variableDefinitionPlaceholder(id: VariableID): string {
  return `${VARIABLE_DEFINITION_PLACEHOLDER_PREFIX}${id}`;
}

/**
 * Given a single value string extracted from a StyleBlock's `styleLess`
 * contents, return `true` if it's a variable reference placeholder value,
 * otherwise return `false`.
 */
export function isVariablePlaceholder(lessValue: string): boolean {
  return /^@var_.*$/.test(lessValue);
}

export function isVariableDefinitionPlaceholder(propName: string): boolean {
  return /^@def_.*$/.test(propName);
}

/**
 * Given a StyleBlock's `styleLess` string contents, return `true` if it
 * contains any variable reference placeholder values, otherwise return `false`.
 */
export function hasVariablePlaceholder(lessValue: string | null): boolean {
  return lessValue
    ? lessValue.split(/[ ,;()]/).some(isVariablePlaceholder)
    : false;
}

/**
 * Given a single value string extracted from a StyleBlock's `styleLess`
 * contents, extract and return a `VariableID` that the value references with a
 * variable placeholder value if applicable, otherwise return `undefined`.
 */
export function getVariableIdFromPlaceholder(
  value: string
): undefined | VariableID {
  const matches = value.match(VARIABLE_PLACEHOLDER_REGEX);
  if (matches != null) {
    return matches[0].split('_')[1];
  }
}

/**
 * Given a single value string extracted from a StyleBlock's `styleLess`
 * contents, extract and return a `VariableID` that the value references _as a
 * definition_ (aka variable override) if applicable, otherwise return
 * `undefined`.
 */
export function getVariableIdFromDefinitionPlaceholder(
  value: string
): undefined | VariableID {
  const match = value
    .matchAll(VARIABLE_DEFINITION_PROPERTY_PLACEHOLDER_REGEX)
    .next().value;
  return match?.[1] as VariableID | undefined;
}

/**
 * INTERNAL USE ONLY
 *
 * Subroutine for extracting variable IDs from an unparsed `styleLess` string.
 *
 * NOTE: Mutates the supplied `variableIds_mutable` `Set`.
 */
const __INTERNAL__extractVariableIdsFromStyleLess = (
  styleLess: string,
  variableIds_mutable: Set<VariableID>
): void => {
  const matches = styleLess.matchAll(VARIABLE_PLACEHOLDER_REGEX);
  for (const [, id] of matches) {
    if (id === undefined) continue;
    variableIds_mutable.add(id);
  }
};

/**
 * Get a set of all variable IDs bound directly in a `StyleBlock`'s style data.
 */
export function getVariableIdsFromStyleBlock(
  styleBlock: StyleBlock
): Set<VariableID> {
  const variableIds_mutable = new Set<VariableID>();
  __INTERNAL__extractVariableIdsFromStyleLess(
    styleBlock.styleLess,
    variableIds_mutable
  );
  styleBlock.variants.forEach((variant) => {
    __INTERNAL__extractVariableIdsFromStyleLess(
      variant.get('styleLess'),
      variableIds_mutable
    );
  });
  return variableIds_mutable;
}

const whiteSpaceRegex = /\s/;
/**
 * Check if a given string character is safe to insert into a CSS variable name
 * without being escaped.
 *
 * To match the CSS spec, we can take a look at the sub-grammar for the built in
 * `<ident-token>` from the CSS specification[1]:
 *
 * ```
 *                      NOTE: We only care about this rule ──╮
 *    ╭────────────────┃`--`┃────────────────╮ ╭─────────────▽──────────────────────╮
 * ││─┼─────────┬┬─┃a-z A-Z _ or non-ASCII┃──┼─┼──┃a-z A-Z 0-9 _ - or non-ASCII┃──┬─┴─││
 *    ╰──┃`-`┃──╯╰────────┃<escape>┃─────────╯ ├─────────────┃<escape>┃───────────┤
 *                                             ╰──────────────────────────────────╯
 * ```
 *
 * Don't worry too much about trying to read this diagram; the important part is
 * what follows the `--` token, which all CSS variable names must start with.
 *
 * This function's goal is to determine if a character is permitted
 * _un-escaped_, so we only need to match the non-escaped portion that follows
 * `--`, or  `a-z A-Z 0-9 _ - or non-ASCII`.
 *
 * One subtle implementation detail: We don't actually allow `-` here, as we
 * want to "collapse" them into individual `-`s within
 * `sanitizeForVariableName`.
 *
 * Sources:
 *
 * [1]: https://www.w3.org/TR/css-syntax-3/#typedef-ident-token
 */
function isNonCollapsablePermittedCharacter(ch: string) {
  const code = ch.charCodeAt(0);
  return (
    // non-ASCII
    (code >= 128 ||
      // a-z
      (code >= 97 && code <= 122) ||
      // A-Z
      (code >= 65 && code <= 90) ||
      // 0-9
      (code >= 48 && code <= 57) ||
      ch === '_') && // Note: The spec technically allows for non-ASCII whitespace characters
    // (such as U+00A0 "no-break space") in CSS variable names, however many
    // parsers treat such characters as separators during tokenization,
    // presumably since the standard whitespace character class (`\s`) includes
    // them.
    //
    // Without this change, our tooling is more liable to crash when rendering
    // CSS (e.g. `lightningcss` has this problem).
    !whiteSpaceRegex.test(ch)
  );
}

/**
 * Given a human-inputted Style Variable name or group name, replace any non-permitted
 * characters with dashes and separate all groups (signified by `/`) with
 * double-dashes (`--`).
 */
export function sanitizeForVariableName(name: string): string {
  const len = name.length;

  let result = '';
  let start = 0;
  let end = len;

  // First, discard characters until we see a permitted character; this "trims"
  // the front of our name:
  for (; start < len; start += 1) {
    const ch = name[start];
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    if (isNonCollapsablePermittedCharacter(ch)) {
      break;
    }
  }

  // Now, discard characters in the same way but from the end of our name; this
  // "trims" the end of our name:
  for (; end >= 1; end -= 1) {
    const ch = name[end - 1];
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    if (isNonCollapsablePermittedCharacter(ch)) {
      break;
    }
  }

  // Now for the fun part: Splitting up our string into groups.
  //
  // We do this by consuming characters until we encounter a `/`, at which point
  // we discard any non-permitted characters until we find our group name's
  // first permitted character, then we repeat until we get to the end of our
  // name string.
  for (; start < end; start += 1) {
    let ch = name[start];
    if (ch === '/') {
      // We've found a group separator, so we've ended our current group.

      // First, we must backtrack any trailing dashes we may have added already,
      // as these will be collapsed into `--` for a group-separator, or removed
      // entirely if we get to the end of our string:
      if (result[result.length - 1] === '-') {
        result = result.substring(0, result.length - 1);
      }

      // Now we start a _nested_ walk, looking for a permitted character, which
      // will be the first character in our next group.
      //
      // If we encounter the start of a new group, we fall out of this nested
      // walk and resume our normal walk looking for the next group separator.
      for (; start < end; start += 1) {
        ch = name[start];
        // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        if (isNonCollapsablePermittedCharacter(ch)) {
          result += '--' + ch;
          break;
        }
      }
      // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    } else if (isNonCollapsablePermittedCharacter(ch)) {
      result += ch;
    } else if (result[result.length - 1] !== '-') {
      // Otherwise we collapse all non-permitted characters into one `-`:
      result += '-';
    }
  }

  return result.toLowerCase();
}

/**
 * Same as `renderCssVariableName`, but includes an error along with the result
 * if there were problems rendering the name.
 */
export function safeRenderCssVariableName(
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'],
  id: VariableID,
  escape: boolean = true
): {error: null | Error; result: string} {
  // Note: When encountering an unknown or deleted variable, we use angle
  // bracket characters (i.e. `--\<deleted\>|foo-...`) here to indicate the
  // nature of the issue, for a couple reasons:
  //
  // 1. These characters will be omitted from user inputted names, so they can
  //    be used to indicate some internal information and should not result in
  //    any naming conflicts.
  // 2. The angle brackets are chosen specifically because other bracket
  //    characters are parsed by an internal CSS utility called `splitCssValue`,
  //    which breaks rendering for some properties which rely on this utility
  //    when rendering CSS.

  let error: Error | null = null;

  const variable = variables.get(id);
  if (!variable) {
    return {
      error,
      result: escape
        ? `\\<unknown\\|${sanitizeForVariableName(id)}\\>`
        : `<unknown|${sanitizeForVariableName(id)}>`,
    };
  }

  let librarySlugPrefix = '';
  if (variable.origin != null) {
    const pluginId = getPluginName(variable.origin);
    const slug = slugMap[pluginId];
    if (slug) {
      librarySlugPrefix = `${slug}`;
    } else {
      // FIXME: Right now there are cases where this ends up getting called
      // after variables load, but before all plugins load, and thus we don't
      // have a `slugMap` to reference to render our styles.
      //
      // Therefore this isn't so much an error as an expected temporary broken
      // state.
      //
      // We probably need a way to track whether our plugins have been loaded
      // and wait for that before we attempt rendering styles.
      //
      // See also: `getUserlandPrefix` which is used for class name prefixing.
      const message = `Could not determine prefix for variable with origin ${pluginId}`;
      error = new Error(message);
      reportBugOnce('[Libraries][renderCssVariableName]', message, {
        data: {variable},
        team: 'structure',
        ticket: 'https://webflow.atlassian.net/browse/SCALE-625',
      });
    }
  }

  let collectionPrefix = '';
  if (variable.collectionId != null) {
    const variableCollection: VariableCollection | undefined =
      variableCollections.get(variable.collectionId);
    if (!variableCollection) {
      const message = `Could not find collection with ID ${variable.collectionId}`;
      error = new Error(message);
      reportBugOnce('[Modes][renderCssVariableName]', message, {
        data: {variable},
        team: 'styles',
        ticket: 'https://webflow.atlassian.net/browse/STYL-1084',
      });
    } else if (!variableCollection.isDefault) {
      // We do not prefix variables from the default/base collection for
      // backwards-compatibility
      collectionPrefix = `_${sanitizeForVariableName(variableCollection.name)}`;
    }
  }

  let result =
    librarySlugPrefix +
    collectionPrefix +
    (librarySlugPrefix || collectionPrefix ? '---' : '') +
    sanitizeForVariableName(variable.name);

  if (variable.deleted) {
    result += escape
      ? `\\<deleted\\|${sanitizeForVariableName(variable.id)}\\>`
      : `<deleted|${sanitizeForVariableName(variable.id)}>`;
  }

  return {error, result};
}

/**
 * Given a variable ID, produce a CSS variable name (without the `--` prefix).
 */
export function renderCssVariableName(
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'],
  id: VariableID,
  escape: boolean = true
): string {
  return safeRenderCssVariableName(
    variables,
    variableCollections,
    slugMap,
    id,
    escape
  ).result;
}

/**
 * Replaces @var_<variable-id> values with CSS variable access syntax (in-designer variable names).
 */
export function replaceVariablePlaceholdersForCSSOM(
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'],
  lessValue: string
) {
  return replaceVariablePlaceholdersForPublish(
    variables,
    variableCollections,
    slugMap,
    lessValue
  );
}

/**
 * Replaces @var_<variable-id> values with CSS variable access syntax (published variable names).
 */
export function replaceVariablePlaceholdersForPublish(
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'],
  lessValue: string,
  mut_seenVariables?: Set<VariableID>
) {
  return lessValue
    .replace(VARIABLE_PLACEHOLDER_REGEX, (fullMatch, variableId) => {
      if (mut_seenVariables) mut_seenVariables.add(variableId);
      return `var(--${renderCssVariableName(variables, variableCollections, slugMap, variableId)})`;
    })
    .replace(
      VARIABLE_DEFINITION_PLACEHOLDER_REGEX,
      (fullMatch, variableId, colonAndValue) => {
        if (mut_seenVariables) mut_seenVariables.add(variableId);
        const property = `--${renderCssVariableName(variables, variableCollections, slugMap, variableId)}`;
        return [property, colonAndValue].join('');
      }
    );
}

/**
 * Replaces @var_<variable-id> values with the actual CSS value of each variable.
 */
export function replaceVariablePlaceholdersForXSCP(
  variables: VariableMap,
  lessValue: string
) {
  return (
    lessValue
      .replace(VARIABLE_PLACEHOLDER_REGEX, (fullMatch, variableId) => {
        const primitive = resolvePreviewValue(variables, variableId);
        if (primitive instanceof Error) {
          // TODO: report bug here (unexpected error resolving variable value)
          return '';
        }

        // When working with a `raw` value that is derived from
        // `resolvePreviewValue`, all variable references should have been
        // replaced with their preview values and thus it should be safe to return
        // the extracted raw value directly here:
        if (primitive.type === 'raw') {
          return primitive.value;
        }
        return convertStylePrimitiveToCSSValue(primitive) ?? '';
      })
      // And we omit any CSS variable overrides:
      .replace(VARIABLE_DEFINITION_PLACEHOLDER_REGEX, '')
  );
}

/**
 * Before migration to TS it allowed to transform variableId from VariableID type to string type
 */
export const convertVariableIdToString: (variableId: VariableID) => string =
  identity;

/**
 * Before migration to TS is allowed to transform variableId from string type to VariableID type
 */
export const convertStringToVariableId: (variableId: string) => VariableID =
  identity;

/**
 * Given a style primitive value returns how value should be represented in CSS.
 */
export const convertStylePrimitiveToCSSValue = (
  primitive: AnyStylePrimitive
): undefined | string => {
  switch (primitive.type) {
    case 'color':
    case 'font-family': {
      return primitive.value;
    }
    case 'length': {
      return primitive.value.value + primitive.value.unit;
    }
    case 'number': {
      return primitive.value.value.toString();
    }
    case 'percentage': {
      return primitive.value.value.toString() + '%';
    }
    default: {
      /*:: (primitive: empty) */
      // TODO: report bug here (unexpected style primitive shape)
      return;
    }
  }
};

/**
 * Given a `VariableMap`, returns a new `VariableMap` with all deleted variables removed.
 */
export const getNonDeletedVariablesOnly = (
  variables: VariableMap
): VariableMap => variables.filter((variable) => !variable.deleted);

/**
 * Given `AnyVariableType[]`, returns a new `AnyVariableType[]` with only the deleted variables.
 */
export const getDeletedVariablesOnly = (variables: VariableMap): VariableMap =>
  variables.filter((variable) => variable.deleted === true);

/**
 * Given a variable, returns a pair of strings: `[variableGroupName, variableName]`.
 * Variable grouping is encoded in the variable's `name` property and is expected to be of
 * the format: `"variableGroupName/variableName"`
 */
export const getVariableGroupAndNamePair = (
  variable: AnyVariableType
): [string, string] => {
  const VARIABLE_NAME_DELIMITER = '/';
  // Takes inputs like `Foo / Bar` or `Foo///Bar` and replaces with `Foo/Bar`:
  const collapsedName = variable.name
    // First, collapse inner slashes:
    .replace(/([\/\s]*)\/+([\/\s]*)/g, '/')
    // Then, remove leading slashes
    .replace(/^[\/\s]/g, '')
    // Finally, remove trailing slashes
    .replace(/[\/\s]+$/g, '');

  const lastDelimiterIdx = collapsedName.lastIndexOf(VARIABLE_NAME_DELIMITER);
  if (lastDelimiterIdx < 0) {
    return ['', collapsedName];
  }
  const groupPart = collapsedName.slice(0, lastDelimiterIdx).trim();
  const namePart = collapsedName.slice(lastDelimiterIdx + 1).trim();
  return [groupPart, namePart];
};

/**
 * Given a variable, determines the `GroupKey` that can be used to retrieve the group
 * the provided variable belongs to from the `GroupedVariables` map.
 * The key is a sanitized string of the user-provided group name
 */
export const getVariableGroupKey = (variable: AnyVariableType): GroupKey => {
  const [varGroupName] = getVariableGroupAndNamePair(variable);
  return sanitizeForVariableName(varGroupName);
};

/**
 * Given a `GroupedVariables` object, returns an array of its `GroupKey`s
 */
export const getGroupKeys = (
  groupedVariables: GroupedVariables
): GroupKey[] => {
  return [...groupedVariables.keys()];
};

/**
 * Given an array of variable ids and a `VariableMap`, returns an array of variables matching
 * the requested ids. Invalid ids that do not exist in the provided `VariableMap` will be ignored.
 *
 * @param ids of variables
 * @param variables a `VariableMap`
 * @returns an array of variables
 */
export const getVariablesByIds = (
  variableIds: VariableID[],
  variables: VariableMap
): Array<AnyVariableType> => {
  return variableIds.reduce<AnyVariableType[]>((res, varId) => {
    const variable = variables.get(varId);
    if (variable) {
      res.push(variable);
    }

    return res;
  }, []);
};

/**
 * Helper function to group variables based on a provided extractor function.
 */
export function groupVariables<T>(
  variables: Iterable<T>,
  getVariable: (item: T) => AnyVariableType
): GroupedVariables {
  const res: GroupedVariables = new Map();

  for (const item of variables) {
    const variable = getVariable(item);
    const groupKey = getVariableGroupKey(variable);
    const currVariablesInGroup = res.get(groupKey) ?? [];
    currVariablesInGroup.push(variable);
    res.set(groupKey, currVariablesInGroup);
  }

  return res;
}

/**
 * Given a `VariableMap`, returns a map where the variables are grouped by variable group names.
 * Variables that do not belong to a group, are grouped under an empty string key.
 */
export const getGroupedVariables = weakMemo(
  (variables: VariableMap): GroupedVariables =>
    groupVariables(variables, identity)
);

/**
 * Creates variable data from the given swatch following id convention for variables that are mirrored from swatches
 */
export const colorVariableFromSwatch = (
  swatch: SwatchType
): ColorVariable | undefined => {
  const parsedColor = parseColor(swatch.color);
  if (parsedColor) {
    return {
      id: convertStringToVariableId(`variable-${swatch.id}`),
      name: swatch.name,
      type: 'color',
      value: {type: 'color', value: parsedColor},
      deleted: false,
      order: null,
      modes: {},
      version: 1,
    };
  }
};

/**
 * Validates that the given swatch has all the required properties for
 * mirroring to a variable. id/name/color must all be defined and non-empty for
 * the swatch to be successfully mirrored.
 */
export function validateSwatchForMirroring(swatch: SwatchType): void {
  if (!swatch.id) {
    throw new Error('[validateSwatchForMirroring] Invalid swatch id');
  }

  if (!swatch.name) {
    throw new Error('[validateSwatchForMirroring] Invalid swatch name');
  }

  if (!swatch.color) {
    throw new Error('[validateSwatchForMirroring] Invalid swatch color');
  }
}

/**
 * Given a `variable`, returns its display name for presentational purposes in the UI
 */
export const getVariableDisplayName = (variable: AnyVariableType) => {
  const [, varName] = getVariableGroupAndNamePair(variable);
  return varName;
};

/**
 * Given a `GroupKey` and a `GroupedVariables` object, returns the group display name.
 * The group display name is determined by the most commonly chosen human-supplied name for variables within a specific group.
 * For example: If 5 variables in a group were named with a  `"Foo"` group prefix, and 4 variables with `"foo"`,
 * the chosen display name will be "Foo".
 */
export const getVariableGroupDisplayName = (
  groupKey: GroupKey,
  groupedVariables: GroupedVariables
): string => {
  const variablesInGroup = groupedVariables.get(groupKey) ?? [];
  if (!variablesInGroup.length) return '';

  const groupNameFormatCounts: Map<string, number> = new Map();
  let maxOccuranceCount = 0;
  let mostCommonGroupName = '';

  variablesInGroup.forEach((variable) => {
    const [groupName] = getVariableGroupAndNamePair(variable);
    const currentCount = groupNameFormatCounts.get(groupName) ?? 0;
    const newCount = currentCount + 1;

    if (newCount > maxOccuranceCount) {
      maxOccuranceCount = newCount;
      mostCommonGroupName = groupName;
    }

    groupNameFormatCounts.set(groupName, newCount);
  });

  return mostCommonGroupName;
};

/**
 * Returns a unique group display name for duplicating `sourceGroupDisplayName`,
 * using `{source} copy`, then `{source} copy 1`, `{source} copy 2`, … when prior
 * names would use the same sanitized group key as other groups in
 * `groupedVariables`.
 */
export const getNextAvailableDuplicateGroupDisplayName = (
  sourceGroupDisplayName: string,
  groupedVariables: GroupedVariables
): string => {
  const usedGroupKeys = new Set<string>();
  for (const groupKey of groupedVariables.keys()) {
    if (groupKey !== '') {
      usedGroupKeys.add(groupKey);
    }
  }

  const baseCopyName = `${sourceGroupDisplayName} copy`;
  const hasUsedGroupKey = (name: string) =>
    usedGroupKeys.has(sanitizeForVariableName(name));

  if (!hasUsedGroupKey(baseCopyName)) {
    return baseCopyName;
  }

  let suffix = 1;
  while (hasUsedGroupKey(`${baseCopyName} ${suffix}`)) {
    suffix += 1;
  }
  return `${baseCopyName} ${suffix}`;
};

export const forTestSuite = {
  getVariableGroupAndNamePair,
  getVariableGroupKey,
  stripTrailingSemicolons,
} as const;

/**
 * Transforms a VariableMap object into a swatches-like data structure. Useful to provide source of truth
 * for ColorSwatchPanel where it is still used in the Designer/Editor UI.
 */
export const cssVariablesToSwatches = (variables: VariableMap): SwatchType[] =>
  variables.reduce<SwatchType[]>((acc, {type, name, id, value, deleted}) => {
    if (type === 'color' && value.type === 'color' && !deleted) {
      acc.push({
        name,
        id,
        color: value.value,
        previewColor: '',
      });
    }
    return acc;
  }, []);

/**
 * Maps a variable type to a common css property declaration that represents the type
 * of value it normally accepts, such as "length" for `width`, "color" for `color`, etc.
 *
 * This is predominantly used in the variables panel for custom css value validation purposes.
 */
export const defaultCssPropertyForVariableType: Record<
  AnyVariableType['type'],
  WebflowCssPropertyFull
> = {
  color: 'color',
  'font-family': 'font-family',
  length: 'width',
  number: 'flex-grow',
  percentage: 'opacity',
} as const;

/**
 * Given lessValue returns all variables ids that are present in the lessValue.
 */
export function getAllVariablesIds(lessValue?: string | null): Array<string> {
  if (!lessValue) return [];
  const ids: Array<string> = [];
  for (const match of lessValue.matchAll(VARIABLE_PLACEHOLDER_REGEX)) {
    const variableId = match[1];
    if (variableId) {
      ids.push(variableId);
    }
  }
  return ids;
}

const replaceStyleLessStyleVariables = weakMemo(
  (variableIdMap: Record<string, string>) => (styleLess: string) =>
    styleLess.replace(VARIABLE_PLACEHOLDER_REGEX, (_fullMatch, variableId) => {
      return `${VARIABLE_PLACEHOLDER_PREFIX}${variableIdMap[variableId] || variableId}`;
    })
);

const replaceStyleVariablesInVariants = weakMemo(
  (variableIdMap: Record<string, string>) =>
    (variants: OrderedMap<BreakpointID, StyleBlockVariant>) =>
      variants.map((variant) =>
        variant.update(
          'styleLess',
          replaceStyleLessStyleVariables(variableIdMap)
        )
      )
);

/**
 * Given a map of variable ids to new variable ids, replaces all variable ids in the style block with the new variable ids.
 */
export const replaceStyleVariablesInStyleBlock =
  (variableIdMap: Record<string, string>) =>
  (styleBlock: StyleBlock): StyleBlock =>
    styleBlock
      .update('styleLess', replaceStyleLessStyleVariables(variableIdMap))
      .update('variants', replaceStyleVariablesInVariants(variableIdMap));

//
// Fractional indexing ordering utils
//

const BASE_95_DIGITS =
  ' !"#$%&\'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~';

/**
 * Small abstraction over `fractional-indexing`'s implementation of
 * `generateNKeysBetween`, with a hard-coded Base95 digits configuration.
 *
 * One key difference from the `fractional-indexing` API: If generation throws
 * for any reason, returns the generic `Error` rather than `throw`ing it.
 */
export const generateNKeysBetween = (
  a: string | null | undefined,
  b: string | null | undefined,
  n: number
): string[] | Error => {
  try {
    return generateNKeysBetween_(a, b, n, BASE_95_DIGITS);
  } catch (e) {
    if (e instanceof Error) {
      return e;
    } else {
      const error = new Error(`Unknown error`);
      error.cause = e;
      return error;
    }
  }
};

/**
 * Small abstraction over `fractional-indexing`'s implementation of
 * `generateKeyBetween`, with a hard-coded Base95 digits configuration.
 *
 * One key difference from the `fractional-indexing` API: If generation throws
 * for any reason, returns the generic `Error` rather than `throw`ing it.
 */
export const generateKeyBetween = (
  a: string | null | undefined,
  b: string | null | undefined
): string | Error => {
  try {
    return generateKeyBetween_(a, b, BASE_95_DIGITS);
  } catch (e) {
    if (e instanceof Error) {
      return e;
    } else {
      const error = new Error(`Unknown error`);
      error.cause = e;
      return error;
    }
  }
};

export const defaultVariableValues: {
  [T in VariableType]: Extract<AnyVariableType, {type: T}>['value'];
} = {
  color: {type: 'color', value: 'white'},
  length: {type: 'length', value: {value: 0, unit: 'px'}},
  'font-family': {type: 'font-family', value: 'Arial'},
  number: {type: 'number', value: {value: 0, unit: '-'}},
  percentage: {type: 'percentage', value: {value: 100, unit: '%'}},
} as const;

type VariableGroupSortInfo_Internal = {
  order: null | string;
  groupKey: string;
  groupDisplayName: string;
};

interface VariableGroupSortInfo {
  groupKey: string;
  groupDisplayName: string;
}

const byDisplayName = (
  itemA: VariableGroupSortInfo_Internal,
  itemB: VariableGroupSortInfo_Internal
): number =>
  itemA.groupDisplayName === ''
    ? -1
    : itemB.groupDisplayName === ''
      ? 1
      : itemA.groupDisplayName.localeCompare(
          itemB.groupDisplayName,
          undefined,
          {numeric: true}
        );

const byOrder = (
  itemA: VariableGroupSortInfo_Internal,
  itemB: VariableGroupSortInfo_Internal
): number => {
  // HACKish: If we encounter a group display name of '' (no group) then we sort
  // by group name...
  if (itemA.groupDisplayName === '' || itemB.groupDisplayName === '') return 0;

  // ...otherwise we sort by order, _then_ display name:
  const orderA = itemA.order;
  const orderB = itemB.order;

  // Maintain original order in the event we have equal order values (e.g. when
  // both are `null`) - technically we don't need this extra check early on, but
  // without it, if we get the order of the checks below wrong, we risk changing
  // the presented order of variables that do not have `order` yet.
  if (orderA === orderB) return 0;

  // Sort `null`s to the front of the list:
  if (orderA == null) return -1;
  if (orderB == null) return 1;

  if (orderA < orderB) return -1;
  if (orderA > orderB) return 1;

  return 0;
};

const byOrderThenByDisplayName = (
  itemA: VariableGroupSortInfo_Internal,
  itemB: VariableGroupSortInfo_Internal
): number => byOrder(itemA, itemB) || byDisplayName(itemA, itemB);

/**
 * Given a `GroupedVariables` object, returns a sorted array of objects containing the key and display name of each group.
 * The items in the array are sorted alphabetically by the group display name using `localCompare`.
 *
 * @param groupedVariables
 * @returns Array<{groupKey: GroupKey, groupDisplayName: string}>
 */
export const getSortedVariableGroupsData = weakMemo(
  (groupedVariables: GroupedVariables): Array<VariableGroupSortInfo> => {
    const groupKeys = getGroupKeys(groupedVariables);
    const groupKeyAndDisplayName = groupKeys.map((key) => {
      return {
        order:
          groupedVariables.get(key)?.find((v) => v.order != null)?.order ??
          null,
        groupKey: key,
        groupDisplayName: getVariableGroupDisplayName(key, groupedVariables),
      };
    });

    const sortedItems = groupKeyAndDisplayName.sort(byOrderThenByDisplayName);
    return sortedItems;
  }
);

export function isValidOrderValue(order: AnyVariableType['order']): boolean {
  if (order == null) return false;

  const result = generateNKeysBetween(order, null, 1);
  if (result instanceof Error) {
    reportBugOnce('InvalidVariableOrderFormat', result.message, {
      team: 'styles',
      ticket: 'https://webflow.atlassian.net/browse/STYL-579',
    });
    return false;
  }
  return true;
}

type StyleVariablesPayload = {
  variables?: ServerAnyVariableType[] | null;
  variableCollections?: ServerVariableCollection[] | null;
};

/**
 * Function that transforms server-side variable and variable collection
 * data into the shape expected by the client
 */
export function processServerStyleVariablesData(
  state: CssVariablesStateType,
  payload: StyleVariablesPayload
): CssVariablesStateType {
  if (!payload || !payload.variables) {
    return state;
  }
  const unorderedVariables: AnyVariableType[] = payload.variables.map(
    (
      // We cast `type` and `value` here as `any` to get around type union woes:
      v: ServerAnyVariableType & {
        type: any;
        value: any;
      }
    ) => ({
      ...v,
      // We cast `modes` here as `any` to get around type union woes:
      modes: (v.modes
        ? v.modes.reduce<NonNullable<AnyVariableType['modes']>>((acc, mode) => {
            acc[mode.modeId] = mode.modeValue;
            return acc;
          }, {})
        : {}) as any,
    })
  );

  if (Array.isArray(payload.variableCollections)) {
    state = assoc('variableCollections')(
      sortByVariableCollectionOrder(
        variableCollectionMapFromArray(
          payload.variableCollections.map((vc) => ({
            ...vc,
            modes: vc.modes.reduce<VariableModesMap>((acc, mode) => {
              acc[mode.id] = mode;
              return acc;
            }, {}),
          }))
        )
      )
    )(state);
  }

  // For "legacy" libraries that have no collections associated, we generate
  // dummy collections to ensure that our UIs function as expected:
  {
    // First we track all plugins that already have default collections:
    const pluginsWithDefaultCollections = new Set<string>();
    for (const collection of state.variableCollections.values()) {
      // If the collection belongs to the site, or it's not a default
      // collection, there's nothing to do...
      if (collection.origin == null || !collection.isDefault) continue;
      // Otherwise we ensure this plugin is tracked as having a default
      // collection already:
      const [pluginId] = collection.origin;
      pluginsWithDefaultCollections.add(pluginId);
    }

    // Now, we check every variable's plugin for an existing default
    // collection...
    for (const variable of unorderedVariables) {
      if (variable.origin == null || variable.collectionId != null) continue;
      const [pluginId] = variable.origin;

      // If this variable's plugin has a default collection, there's nothing
      // to do...
      if (pluginsWithDefaultCollections.has(pluginId)) continue;

      // ...otherwise, we create a dummy default collection...
      const collectionId = `${pluginId}--default-collection`;
      state = {
        ...state,
        variableCollections: applyBrand(
          state.variableCollections.set(collectionId, {
            id: collectionId,
            name: 'Base collection',
            isDefault: true,
            modes: {},
            order: null,
            origin: [pluginId, collectionId],
            version: 1,
          })
        ),
      };
      // ...and make sure to track this plugin as now having a default
      // collection:
      pluginsWithDefaultCollections.add(pluginId);
    }
  }

  /**
   * Given an array of style variables, ensure their `order` keys exist
   * using fractional indexing.
   *
   * The resulting array's variables will each have an `order` field, and
   * the array will be sorted based on `order`.
   *
   * The return value is a tuple of `[AnyVariableType[], needsSync:
   * boolean]`; if any `order` keys needed to be generated, the `needsSync`
   * boolean will be `true`.
   */
  const ensureOrder = (
    _variables: ReadonlyArray<AnyVariableType>
  ): [AnyVariableType[], boolean] | Error => {
    // First group variables into three lists:
    //
    // 1. Ordered variables (variables with `order` values that can be
    //    parsed by `fractional-indexing`)
    // 2. Unordered variables (variables that have `null` or invalid `order`
    //    values)
    // 3. Skipped variables (variables for which we do not want to modify
    //    `order`; e.g. Library variables)
    const ordered: AnyVariableType[] = [];
    const unordered: AnyVariableType[] = [];
    const skipped: AnyVariableType[] = [];

    const groupedVariables = getGroupedVariables(VariableMap.from(_variables));
    const sortedVariableGroupsData =
      getSortedVariableGroupsData(groupedVariables);
    for (const {groupKey} of sortedVariableGroupsData) {
      const variablesInGroup = groupedVariables.get(groupKey);
      if (!variablesInGroup) {
        return new Error(
          `Unable to get groupVariables by groupKey "${groupKey}" - valid groupKeys are ${[...groupedVariables.keys()].map((k) => JSON.stringify(k)).join(', ')}`
        );
      }

      for (const variable of variablesInGroup) {
        if (variable.origin != null) {
          skipped.push(variable);
        } else if (isValidOrderValue(variable.order)) {
          ordered.push(variable);
        } else {
          unordered.push(variable);
        }
      }
    }

    // The order that the variables came in isn't necessarily sorted by
    // `order`, so let's start by doing that, which will allow us to easily
    // determine the last variable in the list which we need anyway.
    ordered.sort((a, b) => ((a.order ?? '') < (b.order ?? '') ? -1 : 1));

    // We then generate all keys for unordered items at once using
    // `generateNKeysBetween`, as this spaces keys out more evenly and
    // produces shorter keys in the scenario where, say, a site is being
    // loaded with a long list of as-yet unordered variables.
    const newOrderKeys = generateNKeysBetween(
      // To append at the end, we "insert between" the last variable and the
      // end of the list, signified by `null`.
      //
      // If no ordered variables exist, we fall back to `null` for the
      // starting key as well, which ensures we evenly distribute order keys
      // between 0 and 1 in the scenario where we're loading a site for the
      // first time:
      ordered.at(-1)?.order ?? null,
      null,
      unordered.length
    );

    if (newOrderKeys instanceof Error) {
      return newOrderKeys;
    }

    // Finally, append each unordered variable to the end of our `ordered`
    // list, providing a properly derived `order` key:
    for (let i = 0; i < newOrderKeys.length; i += 1) {
      const order = newOrderKeys[i];
      const variable = unordered[i];
      if (variable == null || order == null) {
        throw new Error(
          'Unexpected mismatch of unordered variables and newOrderKeys, maybe?'
        );
      }
      ordered.push({
        ...variable,
        order,
      });
    }

    // At this point, our `ordered` array should contain all variables
    // sorted by `order`, so we can return it, prefixed with our skipped
    // variables:
    return [[...skipped, ...ordered], unordered.length > 0];
  };

  const ensureOrderResult = ensureOrder(unorderedVariables);
  if (ensureOrderResult instanceof Error) {
    reportBug('UnableToGenerateVariableOrderKeys', ensureOrderResult.message, {
      team: 'styles',
      ticket: 'https://webflow.atlassian.net/browse/STYL-579',
    });
    const variableMap = variableMapFromArray(unorderedVariables);
    return assoc('variables')(variableMap)(state);
  }

  const [orderedVariables, needsSync] = ensureOrderResult;

  // If we needed to fix any order values, then we need to force those
  // variables to be synced:
  if (needsSync) {
    state = assoc('sync')('OUT_OF_SYNC')(state);
  }

  const variableMap = variableMapFromArray(orderedVariables);

  return assoc('variables')(variableMap)(state);
}

export const ANALYTICS_LOCATIONS = {
  VARIABLES_PANEL: 'variables_panel',
  STYLE_PANEL: 'style_panel',
} as const;

export const getVariableIdsByCssVariableName = weakMemo(
  (slugMap: Plugins.PluginsType['slugMap']) =>
    weakMemo((variableCollections: VariableCollectionMap) =>
      weakMemo((variables: VariableMap): Map<string, VariableID> => {
        const result = new Map<string, VariableID>();
        for (const variable of variables) {
          const cssVariableName = renderCssVariableName(
            variables,
            variableCollections,
            slugMap,
            variable.id
          );
          result.set(`--${cssVariableName}`, variable.id);
        }
        return result;
      })
    )
);

const CSS_VARIABLE_REGEX = /var\(\s*(--[^)]+)\)/g;

/**
 * Replaces any `var(--foo)` variable references that match current known CSS
 * variable names with the appropriate `@var_<variable-id>` placeholders.
 */
export function replaceCssVariableNamesWithVarIds(
  value: string,
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap']
): string {
  const variableIdsByCssVariableName =
    getVariableIdsByCssVariableName(slugMap)(variableCollections)(variables);
  const matches = value.matchAll(CSS_VARIABLE_REGEX);
  let result = value;
  for (const [fullMatch, cssVariableName] of matches) {
    if (cssVariableName == null) continue;
    const variableId = variableIdsByCssVariableName.get(cssVariableName);
    if (variableId == null) continue;
    result = result.replace(fullMatch, variablePlaceholder(variableId));
  }
  return result;
}

/**
 * Given a `VariableMap`, produces a look up set of their CSS variable names
 */
const getCssVariableNames = (
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap']
): Set<string> => {
  const set: Set<string> = new Set();
  variables.forEach((v) => {
    const cssName = renderCssVariableName(
      variables,
      variableCollections,
      slugMap,
      v.id
    );
    set.add(cssName);
  });
  return set;
};

const getVariableGroupKeys = (
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  collectionId: VariableCollectionId | null
): Set<string> => {
  const variableCollection: null | VariableCollection =
    collectionId == null
      ? (variableCollections.find(
          (vc) => Boolean(vc.isDefault) && vc.origin == null
        ) ?? null)
      : (variableCollections.get(collectionId) ?? null);

  if (variableCollection == null) {
    reportBugOnce(
      '[Modes][getVariableGroupKeys]',
      `Could not find collection with ID ${collectionId}`,
      {
        team: 'styles',
        ticket: 'https://webflow.atlassian.net/browse/STYL-1084',
      }
    );
  }

  const variablesInCollection =
    // If we could not determine a variable collection, we operate
    // conservatively on ALL variables.
    variableCollection == null
      ? variables.filter((v) => !v.deleted && v.origin == null)
      : variables.filter(
          (v) =>
            getVariableCollection(v, variableCollections) ===
              variableCollection && !v.deleted
        );

  const groupedVariables = getGroupedVariables(variablesInCollection);
  return new Set(getGroupKeys(groupedVariables));
};

type VariableNameStringValidationFn = (
  newVariableName: string,
  collectionId: VariableCollectionId | null,
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'],
  getExistingCssStrings: () => Set<string>,
  /**
   * When provided, the `newVariableName` will be compared to `currentName` and if both
   * result in the same CSS name, the new name will be considered valid.
   */
  currentName: string | null
) => boolean;

// String validator functions
const isNonEmptyString = (str: string) => Boolean(str);
const isContainingNonSpecialChars = (str: string) =>
  Boolean(sanitizeForVariableName(str));

const TEMP_VARIABLE: ColorVariable = {
  id: 'TEMP_VARIABLE',
  type: 'color',
  value: {type: 'color', value: 'red'},
  name: '',
  order: null,
  modes: {},
  version: 1,
};

const isUniqueVariableCssString: VariableNameStringValidationFn = (
  newVariableName,
  collectionId,
  variables,
  variableCollections,
  slugMap,
  getExistingCssStrings,
  currentName
) => {
  // We generate a temp variable in case we're working with a new unsaved variable
  const tempVariable: AnyVariableType = {
    ...TEMP_VARIABLE,
    collectionId: collectionId ?? undefined,
    name: newVariableName,
  };
  const cssStr = renderCssVariableName(
    variables.upsert(tempVariable),
    variableCollections,
    slugMap,
    tempVariable.id
  );

  if (currentName) {
    const currentTempVariable = {
      ...TEMP_VARIABLE,
      collectionId: collectionId ?? undefined,
      name: currentName,
    };
    const currentCssStr = renderCssVariableName(
      variables.upsert(currentTempVariable),
      variableCollections,
      slugMap,
      currentTempVariable.id
    );

    // If the new user provided variable name is different to the current one,
    // but both names resolve to the same CSS name, we consider the new name valid
    if (cssStr === currentCssStr) {
      return true;
    }
  }
  const existingCssStrings = getExistingCssStrings();
  return !existingCssStrings.has(cssStr);
};

type VariableNameValidationRule = {
  isValidFn: VariableNameStringValidationFn;
  errorLabel: string;
};

/**
 * List of variable name validation rules against which a variable name is checked.
 * Each object in the array contains the following:
 *
 * @isValidFn a validation function that returns `true` if the new name passes the validation check
 * @errorLabel an error to be displayed to the user when the validation check fails
 */
const VARIABLE_NAME_VALIDATION_RULES: Array<VariableNameValidationRule> = [
  {
    isValidFn: isNonEmptyString,
    errorLabel: 'Variable name cannot be empty',
  },
  {
    isValidFn: isContainingNonSpecialChars,
    errorLabel: 'Variable name must contain some non-special characters',
  },
  {
    isValidFn: isUniqueVariableCssString,
    errorLabel: 'This variable name already exists',
  },
];

type VariableGroupNameStringValidationFn = (
  newGroupName: string,
  existingCssStrings: Set<string>,
  /**
   * When provided, the `newGroupName` will be compared to `currentName` and if both
   * result in the same CSS name, the new name will be considered valid.
   */
  currentName: string | null
) => boolean;

type VariableGroupNameValidationRule = {
  isValidFn: VariableGroupNameStringValidationFn;
  errorLabel: string;
};

const isUniqueGroupCssString: VariableGroupNameStringValidationFn = (
  str: string,
  existingCssStrings: Set<string>,
  currentName: string | null
) => {
  const cssStr = sanitizeForVariableName(str);

  if (currentName) {
    const currentCssStr = sanitizeForVariableName(currentName);

    // If the new user provided variable name is different to the current one,
    // but both names resolve to the same CSS name, we consider the new name valid
    if (cssStr === currentCssStr) {
      return true;
    }
  }

  return !existingCssStrings.has(cssStr);
};

/**
 * List of variable group validation rules against which a group name is checked.
 */
const VARIABLE_GROUP_VALIDATION_RULES: Array<VariableGroupNameValidationRule> =
  [
    {
      isValidFn: isNonEmptyString,
      errorLabel: 'Group name cannot be empty',
    },
    {
      isValidFn: isContainingNonSpecialChars,
      errorLabel: 'Group name must contain some non-special characters',
    },
    {
      isValidFn: isUniqueGroupCssString,
      errorLabel: 'This group name already exists',
    },
  ];

/**
 * Given an array of validation rules, runs the `isValid` function for each rule with the provided arguments.
 *
 * @param validationRules array of rules to check
 * @param args provided to each rule's `isValid` function
 * @returns a validation result object. If any rule check fails it will be indicated by
 * the `isValid` flag set to false, and the corresponding `errorLabel` will be included
 */
const runValidators = (
  validationRules: Array<VariableNameValidationRule>,
  args: Parameters<VariableNameStringValidationFn>
) => {
  const maybeValidationError = validationRules.find(
    (validationRule) => validationRule.isValidFn(...args) === false
  );

  return {
    isValid: !maybeValidationError,
    errorLabel: maybeValidationError ? maybeValidationError.errorLabel : '',
  };
};

const runGroupValidators = (
  validationRules: Array<VariableGroupNameValidationRule>,
  args: Parameters<VariableGroupNameStringValidationFn>
) => {
  const maybeValidationError = validationRules.find(
    (validationRule) => validationRule.isValidFn(...args) === false
  );

  return {
    isValid: !maybeValidationError,
    errorLabel: maybeValidationError ? maybeValidationError.errorLabel : '',
  };
};

/**
 *  Given a `newVariableName` and a `cssVariableNames`, validates the new name against a set of rules.
 *  If we are renaming existing variable, provide currentName value in options object.
 *
 * @param newVariableName  The new name to be validated
 * @param cssVariableNames  The names of all existing variables
 * @param options  The options object where `currentName` can be provided
 */
export const validateVariableName = ({
  newVariableName,
  collectionId,
  variables,
  variableCollections,
  slugMap,
  currentName,
}: {
  newVariableName: string;
  collectionId: VariableCollectionId | null;
  variables: VariableMap;
  variableCollections: VariableCollectionMap;
  slugMap: Plugins.PluginsType['slugMap'];
  currentName: string | null;
}) => {
  return runValidators(VARIABLE_NAME_VALIDATION_RULES, [
    newVariableName,
    collectionId,
    variables,
    variableCollections,
    slugMap,
    once(() => getCssVariableNames(variables, variableCollections, slugMap)),
    currentName,
  ]);
};

export const validateVariableGroupName = ({
  newGroupName,
  collectionId,
  variables,
  variableCollections,
  currentName,
}: {
  newGroupName: string;
  collectionId: VariableCollectionId | null;
  variables: VariableMap;
  variableCollections: VariableCollectionMap;
  currentName: string | null;
}) => {
  return runGroupValidators(VARIABLE_GROUP_VALIDATION_RULES, [
    newGroupName,
    getVariableGroupKeys(variables, variableCollections, collectionId),
    currentName,
  ]);
};

/**
 * Computes the `order` value for a newly-created variable, placing it at the
 * end of its group (or at the end of the collection, if it starts a new group).
 *
 * @param variables The full set of existing variables to order against.
 * @param newVariable The variable being created, used to determine its collection, origin, and group.
 * @returns The new `order` string to assign to `newVariable`, or an `Error` if one could not be generated.
 */
export function generateNewVariableOrder(
  variables: VariableMap,
  newVariable: AnyVariableType
): string | Error {
  const pluginId = newVariable.origin
    ? getPluginName(newVariable.origin)
    : null;
  // We need to filter our variables to only include variables that belong to
  // the same collection and origin, because we fully expect to find `order`
  // collisions across variable collections and libraries:
  variables = variables.filter(
    (v) =>
      v.collectionId === newVariable.collectionId &&
      (v.origin ? getPluginName(v.origin) : null) === pluginId
  );

  // We need to determine the `order` value of our newly-created variable.
  let before: AnyVariableType | null = null;
  let after: AnyVariableType | null = null;

  const groupedVariables = getGroupedVariables(variables);
  const groupKey = getVariableGroupKey(newVariable);
  const variablesFromGroup = groupedVariables.get(groupKey);

  // Our TSConfig doesn't think `.findLast` exists, so I'm doing it the old-fashioned way:
  if (variablesFromGroup != null) {
    for (let i = variablesFromGroup.length ?? -1; i >= 0; i -= 1) {
      const groupVariable = variablesFromGroup[i];
      if (groupVariable?.order != null) {
        before = groupVariable;
        break;
      }
    }
  }

  // If we couldn't find a variable in our group with a proper `order`
  // value, insert at the very end of our variables list as a fallback.
  //
  // TODO: We could improve upon this edge-case UX.
  //
  // If we get here, this means that we're creating a new group. We
  // _probably_ want to insert the new group after the "parent" group (if
  // any) as this likely means the user is creating a variable from within
  // an existing group UI. We could also add metadata to the action so we
  // can better decide what to do here.
  if (before == null) {
    before = variables.findLast((v) => v.order != null) ?? null;
  }

  // Now, we want to determine our `after` variable based on whatever
  // `before` ends up being.
  //
  // We want to insert immediately after whatever our `before` is, so we
  // look up the variable's index and choose the one immediately following it.
  const variablesArray = variables.toArray();
  const beforeIndex = before ? variablesArray.indexOf(before) : -1;
  after = variablesArray[beforeIndex + 1] ?? null;

  const newOrder = generateKeyBetween(
    before?.order ?? null,
    after?.order ?? null
  );

  return newOrder;
}

//////////////////////////////////////////////
// START of StyleVariableBindMenuTypes
//

/**
 * The empty state shown when there are no variables to bind to
 **/
export type EmptyStateProps = (arg1: {
  onClick?: () => void;
}) => React.ReactElement;

export type DimmedVariant = 'outline' | 'ghost';
export type ChooseVariablesFn = (variables: VariableMap) => VariableMap;

export type OnChangeMetaType = {
  previousValue?: AnyVariableValue;
  scope?: 'multi';
} & OnChangeMeta;

export type SharedProps = {
  value: AnyVariableValue;
  defaultValue?: AnyVariableValue;
  chooseVariables: ChooseVariablesFn;
  cssPropertyName: WebflowCssPropertyFull;
  validationStrategy: StyleValueValidationStrategy;
  onChange: (
    value: CustomRawPrimitive | AnyVariableValue,
    meta?: OnChangeMetaType
  ) => void;
  disabled?: boolean;
  /**
   * Optional content rendered inside the popover on the variables tab, above the
   * variables list / empty state and separated by a divider. `closePopover`
   * lets injected actions dismiss the popover after acting. `searchQuery` is the
   * live search-box value, so injected options can filter themselves in step
   * with the variables list. Defaults to nothing, so existing consumers are
   * unaffected. Used by IX3 to surface the random/additive value modes alongside
   * variable binding.
   */
  renderExtraOptions?: (
    closePopover: () => void,
    searchQuery?: string
  ) => React.ReactNode;
  /**
   * Overrides the search-box placeholder text. Defaults to
   * `'Search variables, functions '`. IX3 sets this to reflect that property
   * modes (not functions) are searchable in its "Connect" popover.
   */
  searchPlaceholder?: string;
};

export type StylePanelVariableBindMenuProps = {
  children: React.ReactNode;
  cssPropertyName: WebflowCssPropertyFull;
  showCustomTab: boolean;
  shouldUpdateEphemerally?: boolean;
  disableAnalyticsEvents?: boolean;
  style?: React.CSSProperties;
  /**
   * If `false`, hides the pencil icon that appears on hover to edit a bound
   * variable. Defaults to `true`. Use this in IX3 or in places where
   * variable editing should be restricted.
   */
  showEditVariableButton?: boolean;
  /**
   * Custom title for the popover header. Defaults to "Connect".
   */
  popoverTitle?: string;
  /**
   * If `true`, property being bound to is a variable override.
   *
   * Defaults to `false`.
   */
  isVariableOverride?: boolean;
} & Omit<SharedProps, 'defaultValue' | 'validationStrategy'>;

export type TabKey = 'variables' | 'custom';

/**
 * Return type for the `useKeyboardNavigation` hook.
 */
export type UseKeyboardNavigationReturn = {
  setRef: (el: HTMLElement | null) => void;
  tabIndex: number;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onClick: () => void;
  refocus: (focusDir?: 'prev' | 'next') => void;
  onActivateRef: React.MutableRefObject<(e: React.KeyboardEvent) => void>;
};

export type StyleVariableBindMenuProps = SharedProps & {
  /**
   * If `false`, hide the shortcut button that takes you to the variables panel.
   *
   * Defaults to `true`.
   */
  showStyleVariablesPanelButton?: boolean;
  /**
   * If `false`, hide the shortcut button that allows you to open a variable.
   *
   * Defaults to `true`.
   */
  showEditVariableButton?: boolean;
  /**
   * If `false`, hide the `+` button that allows the user to create a new variable.
   *
   * Defaults to `true`.
   */
  showAddButton?: boolean;
  /**
   * If `true`, the Custom tab (for custom value) will be shown.
   *
   * Defaults to `true`.
   */
  showCustomTab?: boolean;
  /**
   * If `true`, hides the unlink button.
   */
  hideUnlinkButton?: boolean;
  /**
   * When `true`, the mask rendered behind the popover will be transparent.
   *
   * When provided a custom `MasksConfig`, it will be used directly.
   *
   * Defaults to `false`.
   */
  transparentMask?: boolean | MasksConfig;

  /**
   *  If `false` the component will not update the style variable ephemerally.
   *  Defaults to `true`.
   */
  shouldUpdateEphemerally?: boolean;
  /**
   * Optional: called when the popover is opened or closed
   */
  onMenuVisibilityChange?: (isVisible: boolean) => void;
  /**
   * The location of the variable binding menu for analytics purposes
   */
  analyticsLocation: string;
  EmptyState: EmptyStateProps;
  ['data-automation-id']?: string;
  cssPropertyName: WebflowCssPropertyFull;
  /**
   * Whether to shift the "dot" binding button to the top-left corner or to
   * leave it within the normal layout.
   *
   * Defaults to `false`.
   */
  offsetDot?: boolean;
  /**
   * Props required for keyboard navigation UX using roving tabindex pattern.
   */
  keyNavProps?: UseKeyboardNavigationReturn;
  tabIndex?: number;

  /**
   * A subset of Spring's Button `variant` prop — 'ghost' | 'outline'
   */
  dimmed?: DimmedVariant;

  /**
   * The id of the variable mode to use for the variable binding menu.
   */
  modeId?: VariableModeId;

  /**
   * Escape hatch to render custom popover triggers/anchors. Uses the same
   * `children` API from the legacy `Popover` component for compatibility.
   */
  children?: (props: RenderChildrenProps) => React.ReactElement<any>;

  /**
   * Custom title for the popover header. Defaults to "Connect".
   */
  popoverTitle?: string;

  /*
   * If provided as a boolean, controls the popover's open/closed state.
   *
   * See also: `onMenuVisibilityChange`
   */
  isOpen?: boolean;

  /**
   * If provided, function that applies an offset to the main popover.
   */
  applyOffset?: (box: Box, currentTab: TabKey) => {top: number; left: number};
};

/**
 * API for a component that works with a variable value, which is used to
 * create/edit variable values from within the bind menu.
 */
export type PopoverVariableEditorComponent = (props: {
  value: AnyVariableValue;
  onChange: (value: AnyVariableValue, meta?: OnChangeMetaType) => void;
  chooseVariables: ChooseVariablesFn;
  cssPropertyName: WebflowCssPropertyFull;
  shouldUpdateEphemerally: boolean;
  StylePanelVariableBindMenu: React.ComponentType<StylePanelVariableBindMenuProps>;
}) => React.ReactNode;

export type ValueEditorComponentProps<T extends AnyVariableType> = {
  value: T['value'];
  onChange: (newValue: T['value']) => void;
  isActive: boolean;
  onToggleEditor: (isOpen: boolean) => void;
  isEditable: boolean;
};

const StyleVariableBindMenuUIContext =
  React.createContext<React.ComponentType<StyleVariableBindMenuProps> | null>(
    null
  );

/**
 * This context tells `StyleVariableBindMenu` what variables are available for
 * binding.
 *
 * For example, if you want to support aliases in the new `VariablesPanel`,
 * you'd wrap the UI with `<StyleVariableBindMenuContext.Provider>` and select
 * the main variables map from the store as per usual.
 *
 * In other places, you might provide variables by using different selectors
 * based on the scope/context; for instance, if/when we support "Style Props",
 * if you're inside a _component_, you may select the global variables from the
 * store, _and_ some component-owned variables from the component definition.
 */
export const StyleVariableBindMenuContext =
  React.createContext<VariableMap>(emptyVariableMap);
StyleVariableBindMenuContext.displayName = 'StyleVariableBindMenuContext';

export function StyleVariableBindMenuUIProvider({
  StyleVariableBindMenu,
  children,
}: {
  StyleVariableBindMenu: React.ComponentType<StyleVariableBindMenuProps>;
  children: React.ReactNode;
}) {
  return (
    <StyleVariableBindMenuUIContext.Provider value={StyleVariableBindMenu}>
      {children}
    </StyleVariableBindMenuUIContext.Provider>
  );
}

export function useStyleVariableBindMenu() {
  const StyleVariableBindMenu = React.useContext(
    StyleVariableBindMenuUIContext
  );
  if (!StyleVariableBindMenu) {
    throw new Error(
      'StyleVariableBindMenu must be used within a StyleVariableBindMenuUIProvider'
    );
  }
  return StyleVariableBindMenu;
}

//
// END of StyleVariableBindMenuTypes
//////////////////////////////////////////////
